// Captures the address link token out of the URL fragment and removes
// it, at MODULE LOAD - imported from index.js right after ./autologin, before
// React renders and before analytics initialises, for the same reason as that
// file: the token is a credential, and must not reach GTM, a referrer or the
// browser history.
//
// The link is .../<orgId>/student-address#t=<token> (billing-address before the
// rename, still accepted). A fragment never goes to a server, so the token is
// also absent from every access log on the way here.
//
// Unlike the autologin token this one is reusable for 30 minutes, so it is kept
// in sessionStorage (this tab only) to survive a reload; it is cleared once the
// server says the link is dead.

const FRAGMENT_KEY = 't';
const STORAGE_KEY = 'og.addressLink';
const PAGE_PATH = /\/(student|billing)-address\/?$/;

let captured = null;

function readStored() {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

function writeStored(entry) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Private browsing can refuse storage; the module copy still serves this load.
  }
}

// Takes the token out of the current URL's fragment, if there is one there.
function captureAndStrip() {
  if (!PAGE_PATH.test(window.location.pathname)) return false;

  try {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = params.get(FRAGMENT_KEY);
    if (!token) return false;

    captured = { token, path: window.location.pathname };
    writeStored(captured);

    params.delete(FRAGMENT_KEY);
    const rest = params.toString();
    // replaceState, not pushState: the token URL must not become a history
    // entry somebody can navigate back to.
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ''}`);
    return true;
  } catch {
    // A URL we cannot parse simply carries no token.
    return false;
  }
}

if (typeof window !== 'undefined') {
  captureAndStrip();
  // A newer link opened in a tab that already shows this page changes only the
  // fragment, which reloads nothing: take its token and start the page again
  // with it, rather than carry on with the one it replaced.
  window.addEventListener('hashchange', () => {
    if (captureAndStrip()) window.location.reload();
  });
}

// The token for this page, from this load or an earlier one in the same tab.
export function getAddressLinkToken() {
  const entry = captured || readStored();
  if (!entry?.token || entry.path !== window.location.pathname) return null;
  return entry.token;
}

export function clearAddressLinkToken() {
  captured = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored.
  }
}
